import { useEffect, useRef, useState } from "react";

const SLIDESHOW_INTERVAL_MS = 5000;
// Aquarium slideshow (photos of the screen) vs. capture grid (extracted fish
// line-art) — tuned independently.
const SLIDE_FILTER = "brightness(110%) contrast(90%) saturate(130%)";
const CAPTURE_FILTER = "brightness(100%) contrast(100%) saturate(200%)";

export default function GalleryApp() {
  const [aquariumItems, setAquariumItems] = useState([]);
  const [scanItems, setScanItems] = useState([]);
  const [status, setStatus] = useState("loading");
  const [zoomed, setZoomed] = useState(null);

  const [slots, setSlots] = useState([null, null]);
  const [activeSlot, setActiveSlot] = useState(0);
  const [slideIdx, setSlideIdx] = useState(0);
  const activeSlotRef = useRef(0);
  const slideIdxRef = useRef(0);

  const goToSlide = (items, i) => {
    const nextSlot = 1 - activeSlotRef.current;
    setSlots((prev) => { const s = [...prev]; s[nextSlot] = items[i]; return s; });
    setActiveSlot(nextSlot);
    activeSlotRef.current = nextSlot;
    setSlideIdx(i);
    slideIdxRef.current = i;
  };

  const load = async () => {
    setStatus("loading");
    // Static manifests generated by scripts/build_gallery_assets.py (no backend).
    const [aquariumRes, scanRes] = await Promise.allSettled([
      fetch("gallery/aquarium.json"),
      fetch("gallery/captures.json"),
    ]);
    try {
      const a = aquariumRes.status === "fulfilled" && aquariumRes.value.ok
        ? await aquariumRes.value.json() : [];
      const s = scanRes.status === "fulfilled" && scanRes.value.ok
        ? await scanRes.value.json() : [];
      setAquariumItems(a);
      setScanItems(s);
      setSlots([a[0] ?? null, null]);
      setActiveSlot(0);
      activeSlotRef.current = 0;
      setSlideIdx(0);
      slideIdxRef.current = 0;
      setStatus("ready");
    } catch {
      setStatus("error");
    }
  };

  const [navTick, setNavTick] = useState(0);

  // Manual step; wraps around. Bumping navTick restarts the auto-advance timer
  // so a manual move doesn't get interrupted a moment later.
  const goRel = (delta) => {
    const n = aquariumItems.length;
    if (n === 0) return;
    goToSlide(aquariumItems, (slideIdxRef.current + delta + n) % n);
    setNavTick((t) => t + 1);
  };

  // The list the zoomed image belongs to, so the lightbox can page through it.
  const zoomedList = zoomed
    ? (zoomed.image.includes("/captures/") ? scanItems : aquariumItems)
    : [];

  const stepZoom = (delta) => {
    if (!zoomed || zoomedList.length === 0) return;
    const idx = zoomedList.findIndex((it) => it.id === zoomed.id);
    if (idx === -1) return;
    const n = zoomedList.length;
    setZoomed(zoomedList[(idx + delta + n) % n]);
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (aquariumItems.length <= 1) return;
    const id = setInterval(() => {
      const next = (slideIdxRef.current + 1) % aquariumItems.length;
      goToSlide(aquariumItems, next);
    }, SLIDESHOW_INTERVAL_MS);
    return () => clearInterval(id);
  }, [aquariumItems, navTick]);

  // Arrow keys page the lightbox when open, otherwise the slideshow. Esc closes.
  useEffect(() => {
    const onKey = (e) => {
      if (zoomed) {
        if (e.key === "Escape") setZoomed(null);
        else if (e.key === "ArrowLeft") stepZoom(-1);
        else if (e.key === "ArrowRight") stepZoom(1);
      } else {
        if (e.key === "ArrowLeft") goRel(-1);
        else if (e.key === "ArrowRight") goRel(1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [aquariumItems, scanItems, zoomed]);

  const activeItem = slots[activeSlot];
  const pad = (n) => String(n).padStart(2, "0");

  return (
    <div className="gallery-app">
      <header className="gallery-header">
        <h1 className="gallery-title">絵が泳ぎだす小さな海</h1>
        <span className="gallery-subtitle">A Small Sea Where Drawings Swim</span>
      </header>

      {status === "loading" ? (
        <div className="gallery-message"><div className="gallery-spinner" /></div>
      ) : null}

      {status === "error" ? (
        <div className="gallery-message">
          <p>読み込みに失敗しました</p>
          <button type="button" className="gallery-retry" onClick={load}>もう一度</button>
        </div>
      ) : null}

      {status === "ready" ? (
        <>
          {aquariumItems.length > 0 ? (
            <figure className="gallery-hero">
              <div className="gallery-plate">
                <div className="gallery-slideshow">
                  {activeItem ? (
                    <img
                      className="gallery-slide-sentinel"
                      src={activeItem.image}
                      style={{ filter: SLIDE_FILTER }}
                      aria-hidden="true"
                    />
                  ) : null}
                  {/* Two persistent slots (stable keys) so toggling `.active` is
                      an opacity change on an existing element — the transition
                      only fires on change, never on mount, so per-item keys
                      would pop. */}
                  {slots.map((item, i) => (
                    <img
                      key={i}
                      className={`gallery-slide-img${i === activeSlot ? " active" : ""}`}
                      src={item ? item.image : undefined}
                      alt={item ? item.label : ""}
                      style={{ filter: SLIDE_FILTER }}
                      onClick={() => item && setZoomed(item)}
                      aria-hidden={item ? undefined : true}
                    />
                  ))}

                  {aquariumItems.length > 1 ? (
                    <>
                      <button type="button" className="gallery-arrow gallery-arrow-prev" onClick={() => goRel(-1)} aria-label="前へ">
                        <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <polyline points="15 6 9 12 15 18" />
                        </svg>
                      </button>
                      <button type="button" className="gallery-arrow gallery-arrow-next" onClick={() => goRel(1)} aria-label="次へ">
                        <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                          <polyline points="9 6 15 12 9 18" />
                        </svg>
                      </button>
                    </>
                  ) : null}
                </div>
              </div>

              <figcaption className="gallery-hero-footer">
                <span className="gallery-slide-label">{activeItem ? activeItem.label : ""}</span>
                {aquariumItems.length > 1 ? (
                  <span className="gallery-counter">
                    {pad(slideIdx + 1)} <span className="gallery-counter-sep">/</span> {pad(aquariumItems.length)}
                  </span>
                ) : null}
              </figcaption>
            </figure>
          ) : null}

          {scanItems.length > 0 ? (
            <p className="gallery-grid-count">全 {scanItems.length} 匹</p>
          ) : null}

          {scanItems.length > 0 ? (
            <div className="gallery-grid">
              {scanItems.map((item) => (
                <button
                  type="button"
                  key={item.id}
                  className="gallery-card"
                  onClick={() => setZoomed(item)}
                >
                  <div className="gallery-thumb">
                    <img
                      src={item.image}
                      alt={item.label}
                      loading="lazy"
                      style={{ filter: CAPTURE_FILTER }}
                    />
                  </div>
                  <span className="gallery-label">{item.label}</span>
                </button>
              ))}
            </div>
          ) : null}
        </>
      ) : null}

      {zoomed ? (
        <div className="gallery-lightbox" onClick={() => setZoomed(null)}>
          <img
            src={zoomed.image}
            alt={zoomed.label}
            style={{ filter: zoomed.image.includes("/captures/") ? CAPTURE_FILTER : SLIDE_FILTER }}
          />
          <span className="gallery-lightbox-label">{zoomed.label}</span>

          {zoomedList.length > 1 ? (
            <>
              <button
                type="button"
                className="gallery-lightbox-arrow gallery-lightbox-prev"
                onClick={(e) => { e.stopPropagation(); stepZoom(-1); }}
                aria-label="前へ"
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="15 6 9 12 15 18" />
                </svg>
              </button>
              <button
                type="button"
                className="gallery-lightbox-arrow gallery-lightbox-next"
                onClick={(e) => { e.stopPropagation(); stepZoom(1); }}
                aria-label="次へ"
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="9 6 15 12 9 18" />
                </svg>
              </button>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
